/**
 * Delegation — the module that has a request name how much is delegated (migration 1036;
 * docs/9-exploration/PRD-delegation-module.md).
 *
 * The model is the Bounded Agency Delegation model: its content lives once, in
 * src/data/boundedAgencyDelegation.ts, and this file only reads it. A request stores the
 * seven-level number; a workspace on the five-level ladder (Michael Hyatt's) shows levels
 * 1, 2, 3, 6 and 7 as 1 to 5 under his names.
 *
 * Reads: commitment_assignment_revisions (the level and the brief's lines) and
 * delegation_ladder(ws). Writes: set_delegation_ladder (admins); the level itself travels
 * with authorize_assignment and respond_to_assignment through assignmentService.
 * The server stays the authority: internal_delegation_terms refuses what this file hides.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';
import { composeDelegationSentence, type RiskBand } from '../data/boundedAgencyDelegation';
import { levelDef, suggestMaxLevel } from './delegationAuthorityLab';
import type { AssignmentRevision, WriteResult } from './assignmentService';

export const DELEGATION_MODULE = 'delegation';

export type DelegationLadder = 'seven' | 'five';

/** The levels a ladder offers, as seven-level numbers. */
export const LADDER_LEVELS: Record<DelegationLadder, readonly number[]> = {
  seven: [1, 2, 3, 4, 5, 6, 7],
  five: [1, 2, 3, 6, 7],
};

/** Hyatt's names for the five degrees (Free to Focus, 2018). */
const FIVE_LEVEL_NAMES: Record<number, string> = {
  1: 'Do as I say',
  2: 'Research the topic',
  3: 'Make a recommendation',
  6: 'Tell me what you did',
  7: 'It’s in your hands',
};

/** "Level 4 · Draft or Prepare for Review", or on the five-level ladder "Level 4 · Tell me what you did". */
export function delegationLevelLabel(level: number, ladder: DelegationLadder = 'seven'): string {
  if (ladder === 'five' && FIVE_LEVEL_NAMES[level]) {
    return `Level ${LADDER_LEVELS.five.indexOf(level) + 1} · ${FIVE_LEVEL_NAMES[level]}`;
  }
  const name = levelDef(level)?.name ?? '';
  // A level 4 or 5 request made before a workspace moved to five levels still reads correctly.
  return ladder === 'five' ? `Level ${level} of 7 · ${name}` : `Level ${level} · ${name}`;
}

/**
 * The highest level the model recommends for work this risky that can or cannot be
 * undone (mirrors delegation_level_cap, 1036). On the five-level ladder a cap of 4 or 5
 * falls to 3, its highest rung at or below it.
 */
export function levelCap(reversible: boolean, risk: RiskBand, ladder: DelegationLadder = 'seven'): number {
  const cap = suggestMaxLevel(reversible ? 'reversible' : 'irreversible', risk);
  return ladder === 'five' && (cap === 4 || cap === 5) ? 3 : cap;
}

/** Which of the brief's lines the level makes required (mirrors internal_delegation_terms). */
export function requiredLines(level: number | null | undefined): { may: boolean; mayNot: boolean; comeBackIf: boolean } {
  const l = level ?? 0;
  return { may: l >= 4, mayNot: l >= 5, comeBackIf: l >= 5 };
}

/** How much is delegated on one revision. */
export interface Delegation {
  level: number;
  risk: RiskBand;
  reversible: boolean;
  aboveReason: string | null;
  may: string | null;
  mayNot: string | null;
  comeBackIf: string | null;
  reportBack: string | null;
}

export function delegationOf(r: Pick<AssignmentRevision,
  'delegation_level' | 'delegation_risk' | 'delegation_reversible' | 'delegation_above_reason'
  | 'delegation_may' | 'delegation_may_not' | 'delegation_come_back_if' | 'delegation_report_back'> | null | undefined): Delegation | null {
  if (!r || r.delegation_level == null) return null;
  return {
    level: r.delegation_level,
    risk: (r.delegation_risk ?? 'moderate') as RiskBand,
    reversible: r.delegation_reversible ?? true,
    aboveReason: r.delegation_above_reason,
    may: r.delegation_may,
    mayNot: r.delegation_may_not,
    comeBackIf: r.delegation_come_back_if,
    reportBack: r.delegation_report_back,
  };
}

/** The fields authorize_assignment and offered terms take. */
export function delegationFields(d: Delegation): Record<string, unknown> {
  return {
    delegation_level: d.level,
    delegation_risk: d.risk,
    delegation_reversible: d.reversible,
    delegation_above_reason: d.aboveReason,
    delegation_may: d.may,
    delegation_may_not: d.mayNot,
    delegation_come_back_if: d.comeBackIf,
    delegation_report_back: d.reportBack,
  };
}

/** The request's one-line readout: the model's delegation sentence, saying only what the request says. */
export function delegationSentence(
  d: Delegation,
  ladder: DelegationLadder,
  request: { outcome?: string | null; doneMeans?: string | null },
  opts: { omitEmpty?: boolean } = { omitEmpty: true },
): string {
  return composeDelegationSentence({
    level: d.level,
    delegate: 'human',
    outcome: request.outcome ?? '',
    authority: d.may ?? '',
    boundaries: d.mayNot ?? '',
    escalation: d.comeBackIf ?? '',
    reporting: d.reportBack ?? '',
    definitionOfDone: request.doneMeans ?? '',
  }, { levelLabel: `${delegationLevelLabel(d.level, ladder)} delegation`, omitEmpty: opts.omitEmpty });
}

/** What Report done asks for at this level, and whether a link to the work belongs with it. */
export function reportDoneWords(level: number | null | undefined): { basisLabel: string; placeholder: string; link: boolean; linkLabel: string } {
  switch (level) {
    case 1: return { basisLabel: 'What was done', placeholder: 'Done exactly as asked', link: false, linkLabel: '' };
    case 2: return { basisLabel: 'Your findings', placeholder: 'What you found, your sources, what is still uncertain', link: true, linkLabel: 'Link to the findings' };
    case 3: return { basisLabel: 'Your recommendation', placeholder: 'What you recommend, the options you weighed, the risks, and the decision needed', link: true, linkLabel: 'Link to the analysis' };
    case 4: return { basisLabel: 'The draft, for review', placeholder: 'What you made, the assumptions you made, and what needs a close look', link: true, linkLabel: 'Link to the draft' };
    case 5: return { basisLabel: 'What was done', placeholder: 'What you did, and any exceptions', link: false, linkLabel: '' };
    case 6: return { basisLabel: 'What you decided', placeholder: 'What you decided, why, what you did, and what follows', link: false, linkLabel: '' };
    case 7: return { basisLabel: 'What was achieved', placeholder: 'The outcome', link: false, linkLabel: '' };
    default: return { basisLabel: 'What was delivered', placeholder: '', link: false, linkLabel: '' };
  }
}

/** Confirm done in the level's words: one act (answer_assignment_response), the level's name for it. */
export function confirmDoneWords(level: number | null | undefined): { accept: string; decline: string; declineLabel: string } {
  if (level === 3) return { accept: 'Approve', decline: 'Not this', declineLabel: 'Why not this recommendation' };
  if (level === 4) return { accept: 'Release', decline: 'Revise', declineLabel: 'What to revise' };
  return { accept: 'Confirm done', decline: 'Not yet', declineLabel: 'What is still missing' };
}

/** The model's own escalation triggers, offered beside a request's "Come back if" lines. */
export const STANDARD_TRIGGERS: readonly string[] = [
  'It would cost more than agreed',
  'It cannot be undone',
  'It would go outside the organization',
  'Legal or compliance exposure',
  'Reputational risk',
  'I am not confident enough',
  'The context changed',
];

/** A level's own prompt for a line, from the model's text (never copied). */
export function levelPrompts(level: number): { may: string; mayNot: string; comeBackIf: string; reportBack: string } {
  const def = levelDef(level);
  return {
    may: def?.agency ?? '',
    mayNot: def?.boundary ?? '',
    comeBackIf: def?.escalation ?? '',
    reportBack: def?.feedback ?? '',
  };
}

/** What the form holds while someone is choosing. */
export interface DelegationDraft {
  level: number | null;
  risk: RiskBand | null;
  reversible: boolean | null;
  aboveReason: string;
  may: string;
  mayNot: string;
  comeBackIf: string;
  reportBack: string;
}

export function draftOf(d: Delegation | null | undefined): DelegationDraft {
  return {
    level: d?.level ?? null,
    risk: d?.risk ?? null,
    reversible: d?.reversible ?? null,
    aboveReason: d?.aboveReason ?? '',
    may: d?.may ?? '',
    mayNot: d?.mayNot ?? '',
    comeBackIf: d?.comeBackIf ?? '',
    reportBack: d?.reportBack ?? '',
  };
}

/** What still keeps this draft from going out (mirrors internal_delegation_terms), in order. */
export function draftGaps(d: DelegationDraft, ladder: DelegationLadder): string[] {
  const gaps: string[] = [];
  if (!d.risk) gaps.push('How risky it is');
  if (d.reversible === null) gaps.push('Whether it can be undone');
  if (d.level === null) { gaps.push('The level'); return gaps; }
  if (d.risk && d.reversible !== null && d.level > levelCap(d.reversible, d.risk, ladder) && !d.aboveReason.trim()) {
    gaps.push('Why it goes above what the model recommends');
  }
  const need = requiredLines(d.level);
  if (need.may && !d.may.trim()) gaps.push('What they may do');
  if (need.mayNot && !d.mayNot.trim()) gaps.push('What they must not do');
  if (need.comeBackIf && !d.comeBackIf.trim()) gaps.push('When to come back');
  return gaps;
}

export function delegationOfDraft(d: DelegationDraft, ladder: DelegationLadder): Delegation | null {
  if (d.level === null || !d.risk || d.reversible === null) return null;
  const above = d.level > levelCap(d.reversible, d.risk, ladder);
  const t = (s: string) => s.trim() || null;
  return {
    level: d.level, risk: d.risk, reversible: d.reversible,
    aboveReason: above ? t(d.aboveReason) : null,
    may: t(d.may), mayNot: t(d.mayNot), comeBackIf: t(d.comeBackIf), reportBack: t(d.reportBack),
  };
}

export async function getDelegationLadder(workspaceId: string): Promise<DelegationLadder> {
  const { data, error } = await getTypedSupabaseClient().rpc('delegation_ladder', { p_workspace_id: workspaceId });
  if (error) throw new Error(error.message);
  return data === 'five' ? 'five' : 'seven';
}

export async function setDelegationLadder(workspaceId: string, ladder: DelegationLadder): Promise<WriteResult<{ ladder: DelegationLadder }>> {
  const { data, error } = await getTypedSupabaseClient().rpc('set_delegation_ladder', { p_workspace_id: workspaceId, p_ladder: ladder });
  if (error) return { ok: false, error: error.message };
  return { ok: true, data: data as unknown as { ladder: DelegationLadder } };
}

/** How much is delegated on each revision named, read from the revision table (RLS: members). */
export async function listRevisionDelegations(revisionIds: readonly string[]): Promise<Map<string, Delegation>> {
  const ids = [...new Set(revisionIds.filter(Boolean))];
  const out = new Map<string, Delegation>();
  if (!ids.length) return out;
  const { data, error } = await getTypedSupabaseClient()
    .from('commitment_assignment_revisions')
    .select('id, delegation_level, delegation_risk, delegation_reversible, delegation_above_reason, delegation_may, delegation_may_not, delegation_come_back_if, delegation_report_back')
    .in('id', ids)
    .not('delegation_level', 'is', null);
  if (error) throw new Error(error.message);
  for (const r of data ?? []) {
    const d = delegationOf(r);
    if (d) out.set(r.id, d);
  }
  return out;
}
